/**
 * N-76 项目名册跟随飞书 —— 企业服务端读飞书「正式立项登记表」与各项目内部表「项目档案」页时
 * 依赖的**列名 / 栏名 / 状态词**，以及状态 → 文件夹访问模式的映射。本文件是这些名字在 DOSIA
 * 侧的唯一来源（服务端对账任务、桌面界面、测试都从这里取，不各写一份）。
 *
 * 这些名字属于 online-projects（催化剂后台）维护的飞书表契约，正本见
 * `online-projects:docs/contracts/README.md`（字段契约 `project-field-map`）。DOSIA 对这两张表
 * **只读、不写**；对方改列名/栏名走其「先改模板、再改契约、最后改表」的流程并通知本仓——本仓
 * 在读取时核对列是否存在、类型是否正确，对不上就整轮不写（fail-closed），不会把「列改名」
 * 读成「没有项目」或「没有成员」。
 *
 * 状态映射是产品经理 2026-09-26 拍板的方案 A（`docs/backlog/requirements/N-76-…md` §4.7）：
 * 执行中可读写；结算类只读；回款完成类归档（从成员本机撤下，服务端保留、可恢复）；
 * 「先款项目」与其它未列出的状态不在范围（不新建、已有的不改）。
 */
import { sanitizeSlug } from "./project-charter.js";
/** 立项登记表里对账必读的非人员列。 */
export const ROSTER_REGISTRY_FIELDS = Object.freeze({
    projectCode: "项目编号",
    projectName: "项目名称",
    status: "当前状态",
    internalSheetLink: "内部表链接",
});
/** 立项登记表里的 8 个人员（飞书 User 类型）列；同名也是项目档案「三、人」一节的栏名。 */
export const ROSTER_PERSON_FIELDS = Object.freeze([
    "商务负责人",
    "PM",
    "AE",
    "投手",
    "策划",
    "话题策略",
    "L2 抽查（小红书）",
    "L2 抽查（策划）",
]);
/** 项目内部表里承载成员 @ 的页签标题。 */
export const ARCHIVE_SHEET_TITLE = "项目档案";
/** 读项目档案页的范围：A 列是栏名、B 列是值（富文本，人员格是 mention 片段）。 */
export const ARCHIVE_READ_RANGE = "A1:B120";
/** 项目档案「三、人」一节的 9 个人员栏：登记表 8 列 + 只存在于档案页的「媒介」。 */
export const ARCHIVE_PERSON_LABELS = Object.freeze([...ROSTER_PERSON_FIELDS, "媒介"]);
/** 登记表「当前状态」→ 共享文件夹访问模式。未列出的状态 = 不在范围。 */
export const ROSTER_STATUS_ACCESS = Object.freeze({
    执行中: "readwrite",
    结算材料准备中: "readonly",
    系统结算中: "readonly",
    回款流程中: "readonly",
    财务确认甲方回款: "readonly",
    项目暂停: "readonly",
    "甲方回款完成，返点待回": "archived",
    回款全部完成: "archived",
    执行完毕: "archived",
});
/** 删人要两轮确认：第一次读到某人不在只记 `removalPendingSince`，间隔至少这么久仍不在才移除。 */
export const ROSTER_REMOVAL_CONFIRM_MIN_MS = 5 * 60 * 1000;
/** N-85 一次性重建后，被取代的旧文件夹保持只读的时长；满这么久对账把它转归档（服务端保留、可恢复）。 */
export const ROSTER_SUPERSEDED_ARCHIVE_AFTER_MS = 7 * 24 * 60 * 60 * 1000;
/** 大面积移出挂起阈值：一轮移除会让非项目管理员成员减少超过这个比例时，本轮不执行该项目的移除。 */
export const ROSTER_MASS_REMOVAL_RATIO = 0.5;
/** 本机没能同步上去、又不能留在原处的员工改动，一律移到 `<visibleRoot>/<此目录>/…` 保存：
 *  被移出项目或项目归档时有未上传改动的文件、只读项目里被改过的文件（原处恢复成服务器版本）、
 *  归档时项目目录里残留的用户文件、别的设备删掉了但本机还改过的个人文件。
 *  该目录不属于任何同步层（桌面 `inferCreateInput` 不认它开头的路径），也不参与冲突副本的定期清理。 */
export const PROJECT_UNSYNCED_RESCUE_DIR = "未同步的项目改动";
/** 服务端对「只读 / 归档项目里新建文件」返回 409 时 `details.code` 的取值；客户端据此不再重试。 */
export const PROJECT_READ_ONLY_ERROR_CODE = "PROJECT_READ_ONLY";
/**
 * 名册对账三个入口（`/api/enterprise/projects/roster-*`）失败时 `error.code` 的取值。
 * 两种 409（没开启 / 上一轮在跑）只能靠这个码区分——客户端**不许**按 `error.message`
 * 的中文字面分支。没带码的失败（例如本组织没绑定登记表）一律按「没有完成」处理。
 */
/** 403：调用者不在项目管理员名单里（桌面据此判定「不显示同步条」）。 */
export const PROJECT_ADMIN_REQUIRED_ERROR_CODE = "PROJECT_ADMIN_REQUIRED";
/** 409：服务器没开名册对账（`DOSIA_PROJECT_ROSTER=off`）。 */
export const ROSTER_DISABLED_ERROR_CODE = "ROSTER_DISABLED";
/** 409：上一轮对账还在进行。 */
export const ROSTER_BUSY_ERROR_CODE = "ROSTER_BUSY";
function isProjectAccessMode(raw) {
    return raw === "readwrite" || raw === "readonly" || raw === "archived";
}
/**
 * 访问模式的反序列化边界（本地镜像列 / 服务端清单条目 / 界面项目列表共用，唯一定义）：
 * - 缺省（undefined / null / 空串）= `readwrite`——N-76 之前的行与旧服务端不带该值；
 * - 三个已知值原样返回；
 * - 其它字符串（更新版本写入、降级回来读到）= `readonly`：看得懂的只有「不是可写」，
 *   按不可写处理，文件仍可读，写由服务端最终裁决。
 */
export function coerceProjectAccessMode(raw) {
    if (raw === undefined || raw === null || raw === "")
        return "readwrite";
    if (isProjectAccessMode(raw))
        return raw;
    return "readonly";
}
/** 状态 → 访问模式；未列出的状态（含空串）返回 null = 不在范围。 */
export function accessModeForStatus(status) {
    if (typeof status !== "string")
        return null;
    const key = status.trim();
    if (!key)
        return null;
    return Object.prototype.hasOwnProperty.call(ROSTER_STATUS_ACCESS, key)
        ? ROSTER_STATUS_ACCESS[key]
        : null;
}
/** 只有「读写」状态的项目会被新建文件夹；只读 / 归档状态的项目不新建。 */
export function isOpenStatus(status) {
    return accessModeForStatus(status) === "readwrite";
}
/**
 * 解析 `DOSIA_PROJECT_ROSTER_BINDINGS`（JSON 数组 `[{orgId, baseToken, tableId}]`）。
 * 空 / 未设置 → 没有绑定、无错误；格式不合法 → 没有绑定 + 错误说明（调用方写启动日志，
 * 视为未配置，绝不部分采用）。同一 orgId 重复出现视为不合法。
 */
export function parseRosterBindings(raw) {
    if (raw === undefined || raw === null || raw.trim() === "")
        return { bindings: [] };
    let parsed;
    try {
        parsed = JSON.parse(raw);
    }
    catch {
        return { bindings: [], error: "DOSIA_PROJECT_ROSTER_BINDINGS 不是合法 JSON" };
    }
    if (!Array.isArray(parsed)) {
        return { bindings: [], error: "DOSIA_PROJECT_ROSTER_BINDINGS 必须是数组" };
    }
    const bindings = [];
    const seen = new Set();
    for (const [index, item] of parsed.entries()) {
        if (!item || typeof item !== "object") {
            return { bindings: [], error: `第 ${index + 1} 项不是对象` };
        }
        const record = item;
        const orgId = typeof record.orgId === "string" ? record.orgId.trim() : "";
        const baseToken = typeof record.baseToken === "string" ? record.baseToken.trim() : "";
        const tableId = typeof record.tableId === "string" ? record.tableId.trim() : "";
        if (!orgId || !baseToken || !tableId) {
            return { bindings: [], error: `第 ${index + 1} 项缺 orgId / baseToken / tableId` };
        }
        if (seen.has(orgId)) {
            return { bindings: [], error: `orgId ${orgId} 重复出现` };
        }
        seen.add(orgId);
        bindings.push({ orgId, baseToken, tableId });
    }
    return { bindings };
}
/** 内部编号的形状：立项编号由后台发号（`ZC…`），带这个前缀才不可能撞上登记表的行。 */
const STANDING_FOLDER_CODE_PATTERN = /^STANDING-[A-Z0-9-]+$/;
/** 这个编号是不是常设文件夹的保留形状（登记表里出现同形的行一律不认）。 */
export function isStandingFolderCode(code) {
    return STANDING_FOLDER_CODE_PATTERN.test(code);
}
/** 常设文件夹总量软上限（2GiB）：文件会同步到每位成员电脑，超了只提醒、不拦上传。 */
export const STANDING_FOLDER_SOFT_LIMIT_BYTES = 2 * 1024 * 1024 * 1024;
function trimmedString(raw) {
    return typeof raw === "string" ? raw.trim() : "";
}
/**
 * 解析 `DOSIA_STANDING_FOLDERS`（JSON 数组）。空 / 未设置 → 没有文件夹、无错误；任何一项不合法 →
 * 没有文件夹 + 错误说明（调用方写启动日志，视为未配置，绝不部分采用，与 `parseRosterBindings` 同口径）。
 */
export function parseStandingFolders(raw) {
    if (raw === undefined || raw === null || raw.trim() === "")
        return { folders: [] };
    let parsed;
    try {
        parsed = JSON.parse(raw);
    }
    catch {
        return { folders: [], error: "DOSIA_STANDING_FOLDERS 不是合法 JSON" };
    }
    if (!Array.isArray(parsed)) {
        return { folders: [], error: "DOSIA_STANDING_FOLDERS 必须是数组" };
    }
    const folders = [];
    const seen = new Set();
    for (const [index, item] of parsed.entries()) {
        if (!item || typeof item !== "object") {
            return { folders: [], error: `第 ${index + 1} 项不是对象` };
        }
        const record = item;
        const orgId = trimmedString(record.orgId);
        const code = trimmedString(record.code);
        const name = trimmedString(record.name);
        const owner = trimmedString(record.owner);
        if (!orgId || !name || !owner) {
            return { folders: [], error: `第 ${index + 1} 项缺 orgId / name / owner` };
        }
        if (!sanitizeSlug(name)) {
            return { folders: [], error: `第 ${index + 1} 项的 name 拼不出文件夹名` };
        }
        if (!STANDING_FOLDER_CODE_PATTERN.test(code)) {
            return { folders: [], error: `第 ${index + 1} 项的 code 必须以 STANDING- 开头，只含大写字母、数字和连字符` };
        }
        const rawMembers = record.members;
        if (!Array.isArray(rawMembers) || rawMembers.length === 0 || rawMembers.some((member) => !trimmedString(member))) {
            return { folders: [], error: `第 ${index + 1} 项的 members 必须是非空的用户名数组` };
        }
        const key = `${orgId}\u0000${code}`;
        if (seen.has(key)) {
            return { folders: [], error: `组织 ${orgId} 的编号 ${code} 重复出现` };
        }
        seen.add(key);
        folders.push({ orgId, code, name, owner, members: [...new Set(rawMembers.map(trimmedString))] });
    }
    return { folders };
}
//# sourceMappingURL=project-roster-contract.js.map